import { toE7 } from '@kmdx/contracts';

/**
 * Телефонът дава позиция САМО в моментите, които имат значение:
 * пристигане на товарене, натоварване, тръгване, пристигане на разтоварване,
 * разтоварване и подписите по ЧМР (изпращач, превозвач, получател). Извън тях телефонът не се пита къде е — позицията на
 * буса идва от бордовото устройство (ADR 0006), а шофьорът не е проследяван
 * лично извън работа. Това е и защитимата по GDPR постановка.
 */

export interface Fix {
  latE7: number;
  lonE7: number;
  accuracyM: number;
}

/**
 * Последна известна точка. Пълни се само когато шофьорът изрично е включил
 * фоновата позиция в „Настройки"; иначе остава празна.
 */
let cachedFix: { fix: Fix; at: number } | null = null;

export function setCachedFix(fix: Fix): void {
  cachedFix = { fix, at: Date.now() };
}

function freshCachedFix(): Fix | null {
  if (!cachedFix) return null;
  return Date.now() - cachedFix.at <= 10 * 60_000 ? cachedFix.fix : null;
}

/**
 * Никога не хвърля и никога не блокира записа на събитието: под товарната рампа
 * в подземен терминал GPS няма и няма да има. Липсваща позиция е допустима,
 * загубено събитие — не.
 */
export function captureFix(timeoutMs = 12_000): Promise<Fix | null> {
  if (!('geolocation' in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const done = (fix: Fix | null) => {
      if (settled) return;
      settled = true;
      resolve(fix);
    };
    const timer = setTimeout(() => done(freshCachedFix()), timeoutMs + 500);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timer);
        done({
          latE7: toE7(position.coords.latitude),
          lonE7: toE7(position.coords.longitude),
          /* Сървърът приема до 10 км — по-грубо е безполезно, но не бива да спира изпращането. */
          accuracyM: Math.min(10_000, Math.round(position.coords.accuracy)),
        });
      },
      () => {
        clearTimeout(timer);
        done(freshCachedFix());
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}

/** Външна навигация: Google Maps и Waze приемат едни и същи параметри. */
export function navigationLinks(input: {
  lat: number | null;
  lon: number | null;
  address: string;
  city: string;
  country: string;
}): { google: string; waze: string } {
  const hasPoint = input.lat !== null && input.lon !== null;
  const query = hasPoint
    ? `${input.lat},${input.lon}`
    : `${input.address}, ${input.city}, ${input.country}`;
  return {
    google: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}&travelmode=driving`,
    waze: hasPoint
      ? `https://waze.com/ul?ll=${input.lat},${input.lon}&navigate=yes`
      : `https://waze.com/ul?q=${encodeURIComponent(query)}&navigate=yes`,
  };
}
